import React, { ChangeEvent, useState, useEffect } from "react";
import { Box, Card, SelectChangeEvent, CircularProgress } from "@mui/material";
import {
  Grid,
  Typography,
  Button,
  IconButton,
  CardContent,
} from "@mui/material";
import CustomInput from "../../components/inputField";
import CustomDropdown from "../../components/drop-down";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch } from "react-redux";
import signUpService from "../../services/signup-service";
import reCAPTCHAkey from "../../utils/reCAPTCHAkey";
import { useNavigate, Link } from "react-router-dom";
import { showToast } from "../../components/toastNotification";
import { DropDown } from "../../types/global";
import i18n from "../../i18n";
import { setMultiLingual } from "../../globalState/actions/headerAction";
import { useAppSelector } from "../../globalState/hooks";
import { HeaderActiveState } from "../../utils/userDetailsCommonType";
import { useTranslation } from "react-i18next";

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  selectLanguage: string;
  selectCourtName: string;
  selectCountyName: string;
  barNumber: string;
}
interface CourtData {
  id: number;
  court_name: string;
}
interface CountryData {
  id: number;
  county_name: string;
}
interface ApiResponse<T> {
  data: T;
}
interface UserRoleData {
  id: number;
  role_name: string;
}
interface UserTypeData {
  id: number;
  user_type_name: string;
}
const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const languageStates = useAppSelector(
    (state: HeaderActiveState) => state.headerURLData.selectedLanguage
  );

  const [signupData, setSignupData] = useState<RegisterData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    selectLanguage: languageStates,
    selectCourtName: "",
    selectCountyName: "",
    barNumber: "",
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errors, setErrors] = useState<Partial<RegisterData>>({});
  const [courtData, setCourtData] = useState<DropDown[]>([]);
  const [countyData, setCountyData] = useState<DropDown[]>([]);
  const [hover, setHover] = useState<boolean>(false);
  const [recaptchaCode, setRecaptchaCode] = useState<string | null>(null);
  const [isRecaptchaValide, setIsRecaptchaValide] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userRoleData, setUserRoleData] = useState<UserRoleData[]>([]);
  const [userTypeData, setUserTypeData] = useState<UserTypeData[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    handleGetCourtListData();
    handleGetCountyListData();
    handleGetUserRoleListData();
    handleGetUserTypeListData();
  }, []);

  const handleInputSelectOnchange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (name === "firstName" || name === "lastName") {
      formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
    }
    if (name === "selectLanguage") {
      i18n.changeLanguage(value);
      dispatch(setMultiLingual(value));
    }
    setSignupData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };
  const handleRecaptcha = (value: string | null) => {
    setRecaptchaCode(value);
    setIsRecaptchaValide("");
  };
  const formValidate = (): boolean => {
    const newErrors: Partial<RegisterData> = {};
    // const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    // const alphabetRegex = /^[A-Za-z\s]+$/;
    const isLocalhost = window.location.hostname === "localhost";

    if (!signupData.firstName.trim()) {
      newErrors.firstName = t("First Name cannot be blank");
    } else if (signupData.firstName.length > 50) {
      newErrors.lastName = t(
        "Ensure this field has not more than 50 characters"
      );
    }
    // else if (specialCharRegex.test(signupData.firstName.trim())) {
    //   newErrors.firstName = t("First Name cannot contain special characters");
    // } else if (!alphabetRegex.test(signupData.firstName.trim())) {
    //   newErrors.firstName = t("First Name can only contain alphabets");
    // }

    if (!signupData.lastName.trim()) {
      newErrors.lastName = t("Last Name cannot be blank");
    } else if (signupData.lastName.length > 50) {
      newErrors.lastName = t(
        "Ensure this field has not more than 50 characters"
      );
    }

    if (!signupData.email.trim()) {
      newErrors.email = t("Email Address cannot be blank");
    } else if (!emailRegex.test(signupData.email.trim().toLowerCase())) {
      newErrors.email = t("Email Address is invalid");
    }

    if (!signupData.password.trim()) {
      newErrors.password = t("Password cannot be blank");
    } else if (signupData.password.length < 8) {
      newErrors.password = t("Password must be 8 characters or more");
    } else if (!/[A-Z]/.test(signupData.password)) {
      newErrors.password = t(
        "Password must contain at least one uppercase letter"
      );
    } else if (!/[a-z]/.test(signupData.password)) {
      newErrors.password = t(
        "Password must contain at least one lowercase letter"
      );
    } else if (!/[0-9]/.test(signupData.password)) {
      newErrors.password = t("Password must contain at least one number");
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(signupData.password)) {
      newErrors.password = t(
        "Password must contain at least one special character"
      );
    }

    if (!isLocalhost && recaptchaCode === null) {
      setIsRecaptchaValide(t("Please confirm that you are not a bot."));
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGetCourtListData = () => {
    signUpService
      .getCourtListAPI()
      .then((response: { data: ApiResponse<CourtData[]> }) => {
        if (response?.data?.data?.length > 0) {
          const finalData: DropDown[] = response.data.data.map(
            (court: CourtData) => ({
              value: court.id.toString(),
              label: court.court_name,
            })
          );
          setCourtData(finalData);
        } else {
          setCourtData([]);
        }
      })
      .catch((error) => {
        console.log(error, t("API Error"));
      });
  };
  const handleGetCountyListData = () => {
    signUpService
      .getCountyListAPI()
      .then((response: { data: ApiResponse<CountryData[]> }) => {
        if (response?.data.data?.length > 0) {
          const finalData: DropDown[] = response.data.data.map(
            (item: CountryData) => ({
              value: item.id.toString(),
              label: item.county_name,
            })
          );
          setCountyData(finalData);
        } else {
          setCountyData([]);
        }
      })
      .catch((error) => {
        console.log(error, t("API Error"));
      });
  };
  const handleGetUserRoleListData = () => {
    signUpService
      .getUserRoleListAPI()
      .then((response: { data: ApiResponse<UserRoleData[]> }) => {
        if (response?.data?.data?.length > 0) {
          setUserRoleData(response.data.data);
        } else {
          setUserRoleData([]);
        }
      })
      .catch((error) => {
        console.log(error, t("User Information Error"));
      });
  };
  const handleGetUserTypeListData = () => {
    signUpService
      .getUserTypeListAPI()
      .then((response: { data: ApiResponse<UserTypeData[]> }) => {
        if (response?.data.data?.length > 0) {
          setUserTypeData(response.data.data);
        } else {
          setUserTypeData([]);
        }
      })
      .catch((error) => {
        console.log(error, t("User Information Error"));
      });
  };
  const handleSignUpSubmit = async () => {
    if (formValidate()) {
      const isLocalhost = window.location.hostname === "localhost";
      if (isLocalhost || recaptchaCode !== null) {
        setIsLoading(true);
        const roleId = userRoleData.filter((x) => x.role_name === "User")[0].id;
        const userTypeId = userTypeData.filter(
          (x) => x.user_type_name === "User"
        )[0].id;
        const normalizedEmail = signupData.email.trim().toLowerCase();
        const SignUpInputData = {
          first_name: signupData.firstName,
          last_name: signupData.lastName,
          email: normalizedEmail,
          password: signupData.password,
          language: signupData.selectLanguage === "" ? "english" : "english",
          court_id: signupData.selectCourtName,
          county_id: signupData.selectCountyName,
          bar_number: signupData.barNumber,
          user_type_id: userTypeId,
          role_id: roleId,
        };
        const inputData: { email: string; recaptcha_response?: string } = {
          email: normalizedEmail,
        };
        if (!isLocalhost) {
          inputData.recaptcha_response = recaptchaCode!;
        }
        await signUpService
          .postSignUpOTP(inputData)
          .then((response) => {
            if (response && response.status === 200) {
              showToast(t(response.data.message), "success");
              navigate("/signUpOTP", {
                state: SignUpInputData,
              });
            } else {
              showToast(t(response?.data?.message), "error");
            }
            setIsLoading(false);
          })
          .catch((error) => {
            setIsLoading(false);
            console.log(t(error), "API Error");
          });
      } else {
        setIsRecaptchaValide(t("Please confirm that you are not a bot."));
      }
    }
  };
  const siteKey = (() => {
    const host = window.location.host;

    if (host === "localhost:3000") {
      return reCAPTCHAkey.localhostSitekey;
    } else if (host === "childsupport-dev.brainvire.net") {
      return reCAPTCHAkey.devSitekey;
    } else {
      return reCAPTCHAkey.UATSitekey;
    }
  })();
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            minWidth: 200,
            maxWidth: { xs: 315, sm: 600, md: 750 },
            my: 5,
            mx: 2,
            p: 2,
            bgcolor: "#fff",
            border: "1px solid #DFDFDF",
            borderRadius: "30px 30px",
            opacity: 1,
          }}
        >
          <Box sx={{ marginTop: "2rem" }}>
            <Box>
              <Typography
                variant="h4"
                color="#0464AB;"
                textAlign="center"
                gutterBottom
                sx={{
                  fontFamily: "DM Serif Display",
                }}
                className="h4 main_heading"
              >
                {t("Create Your Account")}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #DFDFDF",
                pb: 1,
                px: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                fontSize={14}
                fontFamily={"Inter"}
                marginRight={1}
                textAlign="center"
              >
                {t(
                  "Enter a valid email address that will serve as your user name. If you do not already have an email address, please create one first. Your password must meet state of Georgia security standards and consist of the following:"
                )}
              </Typography>

              <Box
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                sx={{ position: "relative" }}
              >
                <InfoOutlinedIcon
                  sx={{
                    color: "#FBB522",
                    cursor: "pointer",
                    fontSize: 20,
                  }}
                />
                {hover && (
                  <Card
                    sx={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      width: { xs: 300, sm: 350, md: 350 },
                      boxShadow: 3,
                      zIndex: 1,
                      mt: 1,
                      borderRadius: "20px",
                    }}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                  >
                    <CardContent
                      sx={{
                        paddingBottom: "8px !important",
                        fontFamily: "Inter",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        fontSize={14}
                        sx={{
                          color: "#0464AB",
                          fontWeight: "bold",
                        }}
                      >
                        {t(
                          "Password length must be at least eight characters, and must include the following"
                        )}
                        :
                      </Typography>
                      <Box m={1}>
                        <ul>
                          <li>
                            {t("one upper-case and one lower-case letter;")}
                          </li>
                          <li>{t("one or more numbers; and")}</li>
                          <li>
                            {t(
                              "one or more special characters, such as @, #, $."
                            )}
                          </li>
                        </ul>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography
              m={2}
              fontWeight="bold"
              gutterBottom
              sx={{ color: "#022E4C", fontSize: "14px" }}
            >
              {t("User Information")}
            </Typography>
            <Box mx={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <CustomInput
                    name="firstName"
                    label={t("First Name")}
                    value={signupData.firstName}
                    onChange={handleInputSelectOnchange}
                    placeholder={t("First Name")}
                    required={true}
                    error={errors.firstName}
                    type="text"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomInput
                    name="lastName"
                    label={t("Last Name")}
                    value={signupData.lastName}
                    onChange={handleInputSelectOnchange}
                    placeholder={t("Last Name")}
                    required={true}
                    error={errors.lastName}
                    type="text"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <CustomInput
                    onChange={handleInputSelectOnchange}
                    value={signupData.email}
                    label={t("Email")}
                    name="email"
                    type="text"
                    required={true}
                    error={errors.email}
                    placeholder={t("Email")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomInput
                    onChange={handleInputSelectOnchange}
                    value={signupData.password}
                    label={t("Password")}
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required={true}
                    error={errors.password}
                    placeholder={t("Password")}
                    endAdornment={
                      <IconButton
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOutlinedIcon />
                        ) : (
                          <VisibilityOffOutlinedIcon />
                        )}
                      </IconButton>
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <CustomDropdown
                    name="selectLanguage"
                    label={t("Default Language")}
                    value={signupData.selectLanguage}
                    onChange={handleInputSelectOnchange}
                    options={[
                      { value: "en", label: "English" },
                      { value: "es", label: "Spanish" },
                    ]}
                    placeholder={t("- Select -")}
                  />
                </Grid>
              </Grid>
            </Box>
            <Typography
              mx={2}
              mt={1}
              fontWeight="bold"
              gutterBottom
              sx={{ color: "#022E4C", fontSize: "14px" }}
            >
              {t("Administration")}
            </Typography>
            <Box m={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <CustomDropdown
                    name="selectCourtName"
                    label={t("Default Court Name")}
                    value={signupData.selectCourtName}
                    onChange={handleInputSelectOnchange}
                    options={courtData}
                    placeholder={t("- Select -")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomDropdown
                    name="selectCountyName"
                    label={t("Default County Name")}
                    value={signupData.selectCountyName}
                    onChange={handleInputSelectOnchange}
                    options={countyData}
                    placeholder={t("- Select -")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomInput
                    name="barNumber"
                    label={t("Bar Number")}
                    value={signupData.barNumber}
                    onChange={handleInputSelectOnchange}
                    placeholder={t("Bar Number")}
                    type="number"
                  />
                </Grid>
              </Grid>
              {window.location.hostname !== "localhost" && (
                <Box sx={{ mb: 3, mt: 3 }}>
                  <ReCAPTCHA sitekey={siteKey} onChange={handleRecaptcha} />
                  {isRecaptchaValide !== "" && (
                    <span style={{ color: "#d32f2f" }}> {isRecaptchaValide}</span>
                  )}
                </Box>
              )}
            </Box>
            {/* <Grid item xs={12} md={12} m={5}> */}
            <Box display="flex" justifyContent="flex-start" mb={5}>
              <Button
                variant="contained"
                disableElevation
                disabled={isLoading}
                onClick={handleSignUpSubmit}
                sx={{
                  borderRadius: "20px",
                  width: "fit-content",
                  mx: 2,

                  fontWeight: "600",
                  backgroundColor: "#0464AB",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#FBB522",
                    color: "#022E4C",
                    fontWeight: "600",
                  },
                }}
              >
                {t("Create New User")}
                {isLoading && (
                  <CircularProgress size={18} sx={{ padding: "0px 8px" }} />
                )}
              </Button>
              <Link to="/">
                <Button
                  variant="outlined"
                  disableElevation
                  sx={{
                    borderRadius: "20px",
                    width: 100,
                    textTransform: "none",
                    backgroundColor: "#fff",
                    border: "1px solid #0464AB",
                    color: "#0464AB",
                    fontWeight: "600",
                    "&:hover": {
                      backgroundColor: "#FBB522",
                      color: "#022E4C",
                      border: "none",
                      fontWeight: "600",
                    },
                  }}
                >
                  {t("Cancel")}
                </Button>
              </Link>
            </Box>
            {/* </Grid> */}
          </Box>
        </Card>
      </Box>
    </>
  );
};

export default RegisterForm;
